import axios from "axios";
import {
  useSignInMutation,
} from "../store/services/userApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../Appjs/api.js";

/* ------------------------------------------------------------------ */
/*  HARD RELOAD — wipes Cache Storage + service workers, then does a   */
/*  cache-busted navigation so the browser re-fetches HTML & assets.   */
/* ------------------------------------------------------------------ */
const hardReload = async (path = "/") => {
  // 1) Wipe Cache Storage (PWA / service-worker caches)
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (err) {
    console.warn("Cache storage clear failed:", err);
  }

  // 2) Unregister any service workers so a stale shell can't be served
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (err) {
    console.warn("Service worker unregister failed:", err);
  }

  // 3) Cache-busting navigation → bypasses the HTTP disk cache
  const bust = `${path}${path.includes("?") ? "&" : "?"}_cb=${Date.now()}`;
  window.location.replace(bust);
};

/* ------------------------------------------------------------------ */
/*  Palette — pulled from the Navratri banner                          */
/* ------------------------------------------------------------------ */
const C = {
  gold: "#F6C453",
  goldSoft: "#FFE3A3",
  amber: "#F9A826",
  ember: "#F26A1B",
  crimson: "#C2311B",
  deepRed: "#8E1B12",
  teal: "#0E3A4C",
  midnight: "#071C28",
  cream: "#FFF8EC",
  sand: "#EADBC0",
};

const EMBER_COUNT = 30;

const Login = () => {
  const navigate = useNavigate();
  const url = window.location.origin;
  const [isChecked, setIsChecked] = useState(false);
  const [company, setCompany] = useState("");
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [apis, setApis] = useState(false);
  const [image, setImage] = useState(null);
  const [isactive, setIsActive] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState("");

  const [signIn] = useSignInMutation();

  // Fixed values so the embers don't jump on re-render
  const embers = useMemo(
    () =>
      Array.from({ length: EMBER_COUNT }, (_, i) => ({
        left: (i * 37 + 9) % 100,
        size: 2 + ((i * 3) % 5),
        dur: 10 + ((i * 7) % 12),
        delay: -((i * 1.7) % 20),
        dx: (i % 2 ? 1 : -1) * (15 + ((i * 11) % 55)),
      })),
    []
  );

  const handleCheckboxChange = (e) => setIsChecked(e.target.checked);

  const getDomainInfo = useCallback(async () => {
    try {
      const res = await axios.get(
        `${api}/common/get-doamain-details?domain=${url}`,
        { withCredentials: true }
      );

      setCompany(res?.data?.data?.company_name || "Welcome Back");
      setIsActive(res?.data?.data?.is_active ?? true);
      setImage(res?.data?.data?.reseller_logo || null);
    } catch (error) {
      console.error("❌ Error fetching domain info:", error);

      // local me API fail ho tab bhi login form show hoga
      setCompany("Welcome Back");
      setIsActive(true);
      setImage(null);
    } finally {
      setApis(true);
    }
  }, [url]);

  useEffect(() => { getDomainInfo(); }, [getDomainInfo]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) navigate("/login");
    else navigate("/");
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await signIn(formData).unwrap();

      if (response.requirePasswordChange) {
        localStorage.setItem("accessToken", response.accessToken);
        localStorage.setItem("refreshToken", response.refreshToken);
        localStorage.setItem("reseller", response.reseller ? "true" : "false");
        window.dispatchEvent(new Event("storage"));
        // HARD reload into the change-password screen
        await hardReload("/initial-change-password");
        return;
      }

      if (response.enableOtp) {
        localStorage.setItem("username", formData.username);
        navigate("/verify-otp", {
          state: { mobiles: response.mobiles, autoNavigate: true },
        });
        return;
      }

      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("refreshToken", response.refreshToken);
      localStorage.setItem("reseller", response?.reseller ? "true" : "false");
      window.dispatchEvent(new Event("storage"));

      // ✅ HARD reload to home — cache cleared, fresh bundle pulled
      await hardReload("/");
    } catch (err) {
      setError(err.data?.message || "Something went wrong");
      console.error("Login error:", err);
    }
  };

  const styles = {
    page: {
      height: "100vh",
      minHeight: "100dvh",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: C.midnight,
      position: "relative",
      overflow: "hidden",
      padding: 0,
      fontFamily: "'Nunito', 'Segoe UI', sans-serif",
      boxSizing: "border-box",
    },
    shell: {
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: 0,
      display: "flex",
      overflow: "hidden",
      background: C.midnight,
    },
    leftPanel: {
      position: "relative",
      flex: "1.2",
      display: "flex",
      boxSizing: "border-box",
      padding: "clamp(14px, 1.8vw, 26px)",
      overflow: "hidden",
      background: `
        radial-gradient(ellipse 75% 45% at 15% 105%, rgba(242,106,27,0.42) 0%, rgba(242,106,27,0) 65%),
        radial-gradient(ellipse 60% 40% at 90% -5%, rgba(246,196,83,0.18) 0%, rgba(246,196,83,0) 65%),
        linear-gradient(150deg, #4a130d 0%, ${C.teal} 62%, ${C.midnight} 100%)`,
    },
    bannerCard: {
      position: "relative",
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      overflow: "hidden",
      borderRadius: "28px",
      border: "1px solid rgba(246, 196, 83, 0.7)",
      boxShadow:
        "0 28px 70px rgba(0, 0, 0, 0.55), 0 0 70px rgba(242, 106, 27, 0.25)",
      background: C.deepRed,
    },
    navratriImage: {
      position: "absolute",
      inset: 0,
      zIndex: 0,
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
    },
    vignette: {
      position: "absolute",
      inset: 0,
      zIndex: 1,
      pointerEvents: "none",
      background:
        "radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) 55%, rgba(7,28,40,0.55) 100%)",
    },
    bottomScrim: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: "34%",
      zIndex: 1,
      pointerEvents: "none",
      background: `linear-gradient(to top, rgba(7,28,40,0.85) 0%, rgba(7,28,40,0) 100%)`,
    },
    navratriWish: {
      position: "absolute",
      right: "6%",
      bottom: "7%",
      zIndex: 4,
      margin: 0,
      maxWidth: "min(380px, 46%)",
      textAlign: "right",
      color: C.goldSoft,
      fontSize: "clamp(14px, 1.25vw, 18px)",
      fontWeight: "700",
      lineHeight: "1.55",
      textShadow: "0 2px 14px rgba(0, 0, 0, 0.7)",
    },
    rightPane: {
      flex: "0.82",
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px 22px",
      borderLeft: "1px solid rgba(246, 196, 83, 0.45)",
      background: `
        radial-gradient(ellipse 70% 55% at 10% 105%, rgba(242,106,27,0.38) 0%, rgba(242,106,27,0) 65%),
        radial-gradient(ellipse 60% 45% at 95% -5%, rgba(246,196,83,0.16) 0%, rgba(246,196,83,0) 65%),
        linear-gradient(165deg, ${C.teal} 0%, #0A2B3A 55%, ${C.midnight} 100%)`,
    },
    card: {
      position: "relative",
      width: "100%",
      maxWidth: "420px",
      background: `linear-gradient(180deg, #FFFCF5 0%, ${C.cream} 100%)`,
      borderRadius: "26px",
      border: "1px solid rgba(246, 196, 83, 0.6)",
      boxShadow:
        "0 30px 70px rgba(0, 0, 0, 0.45), 0 0 60px rgba(242, 106, 27, 0.16)",
      padding: "18px 24px 16px",
      animation: "slideUp 0.7s cubic-bezier(0.22,1,0.36,1) both",
    },
    cardAccent: {
      position: "absolute",
      top: 0,
      left: "18%",
      right: "18%",
      height: "3px",
      borderRadius: "0 0 6px 6px",
      background: `linear-gradient(90deg, rgba(246,196,83,0) 0%, ${C.amber} 30%, ${C.ember} 70%, rgba(242,106,27,0) 100%)`,
    },
    logoWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "64px",
      marginBottom: "8px",
    },
    title: {
      textAlign: "center",
      marginBottom: "4px",
      fontFamily: "'Marcellus', 'Nunito', serif",
      fontSize: "26px",
      fontWeight: "400",
      color: C.teal,
      letterSpacing: "0.2px",
      lineHeight: "1.15",
    },
    subtitle: {
      textAlign: "center",
      fontSize: "14px",
      color: "#6a6f6f",
      marginBottom: "14px",
      fontWeight: "500",
    },
    errorBox: {
      background: "rgba(194,49,27,0.1)",
      border: "1px solid rgba(194, 49, 27, 0.3)",
      borderRadius: "12px",
      padding: "8px 12px",
      marginBottom: "12px",
      fontSize: "12px",
      color: C.deepRed,
      textAlign: "center",
      fontWeight: "700",
    },
    fieldWrapper: {
      marginBottom: "10px",
      position: "relative",
    },
    label: (isFocused) => ({
      display: "block",
      fontSize: "12.5px",
      fontWeight: "700",
      color: isFocused ? C.crimson : C.teal,
      marginBottom: "6px",
      transition: "color 0.2s",
    }),
    input: (isFocused) => ({
      width: "100%",
      padding: "10px 12px 10px 38px",
      background: "#fff",
      border: isFocused ? `1.5px solid ${C.ember}` : `1.5px solid ${C.sand}`,
      borderRadius: "14px",
      fontSize: "14px",
      color: "#1d2a30",
      outline: "none",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      boxShadow: isFocused ? "0 0 0 4px rgba(242, 106, 27, 0.14)" : "none",
      fontFamily: "inherit",
    }),
    inputIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: "15px",
      color: "#7a7a7a",
    },
    passwordToggle: {
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "#787878",
      fontSize: "15px",
      padding: "0",
      fontFamily: "inherit",
    },
    checkboxLabel: {
      fontSize: "12.5px",
      color: "#5d6668",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "12px",
      userSelect: "none",
      lineHeight: "1.5",
    },
    checkboxInput: {
      width: "16px",
      height: "16px",
      accentColor: C.ember,
      cursor: "pointer",
      flexShrink: 0,
    },
    termsLink: {
      color: C.crimson,
      textDecoration: "none",
      fontWeight: "700",
    },
    submitBtn: (disabled) => ({
      width: "100%",
      padding: "13px 16px",
      background: disabled
        ? "#EADFCB"
        : `linear-gradient(135deg, ${C.amber} 0%, ${C.ember} 48%, ${C.crimson} 100%)`,
      border: "none",
      borderRadius: "14px",
      color: disabled ? "rgba(110,85,55,0.65)" : "#fff",
      fontSize: "16px",
      fontWeight: "800",
      cursor: disabled ? "not-allowed" : "pointer",
      letterSpacing: "0.3px",
      transition: "all 0.25s",
      boxShadow: disabled ? "none" : "0 12px 28px rgba(242, 106, 27, 0.38)",
      fontFamily: "inherit",
      transform: "translateY(0)",
    }),
    forgotLink: {
      display: "block",
      textAlign: "right",
      marginTop: "2px",
      fontSize: "13px",
      color: C.crimson,
      textDecoration: "none",
      fontWeight: "700",
      opacity: 0.9,
      transition: "opacity 0.2s",
    },
  };

  const keyframes = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&family=Marcellus&display=swap');

    @keyframes slideUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes sparkle {
      0%, 100% { opacity: .5; }
      50% { opacity: 1; }
    }
    input::placeholder { color: rgba(118, 124, 130, 0.8); }
    .login-btn:hover:not(:disabled) {
      transform: translateY(-1px) !important;
      box-shadow: 0 16px 34px rgba(242, 106, 27, 0.5) !important;
    }
    .login-btn:active:not(:disabled) { transform: translateY(0px) !important; }
    .login-btn:focus-visible { outline: 3px solid rgba(246,196,83,.8); outline-offset: 2px; }
    .forgot-link:hover { opacity: 1 !important; text-decoration: underline; }

    /* ---------- Banner card ---------- */
    .banner-card::after {
      content: ""; position: absolute; inset: 10px; z-index: 3; pointer-events: none;
      border: 1px solid rgba(246, 196, 83, 0.55);
      border-radius: 19px;
    }

    /* ---------- Banner life ---------- */
    .banner-img {
      transform-origin: 68% 18%;
      animation: kenburns 30s ease-in-out infinite alternate;
    }
    @keyframes kenburns {
      from { transform: scale(1); }
      to   { transform: scale(1.09); }
    }

    /* soft aura around the Devi's halo */
    .banner-aura {
      position: absolute; inset: 0; z-index: 1; pointer-events: none;
      mix-blend-mode: screen;
      background: radial-gradient(circle at 68% 15%, rgba(255,214,120,.55) 0%, rgba(255,170,60,.18) 18%, rgba(255,170,60,0) 38%);
      animation: aura 7s ease-in-out infinite;
    }
    @keyframes aura {
      0%, 100% { opacity: .45; }
      50%      { opacity: 1; }
    }

    /* rising embers */
    .ember {
      position: absolute; bottom: -12px; z-index: 3; pointer-events: none;
      border-radius: 50%;
      background: radial-gradient(circle, #FFF1C2 0%, #FFB347 45%, rgba(255,120,30,0) 72%);
      box-shadow: 0 0 8px 2px rgba(255,170,60,.65);
      opacity: 0;
      animation: rise var(--dur) linear infinite;
      animation-delay: var(--delay);
    }
    @keyframes rise {
      0%   { opacity: 0;  transform: translate(0, 0) scale(.6); }
      12%  { opacity: 1; }
      80%  { opacity: .75; }
      100% { opacity: 0;  transform: translate(var(--dx), -105vh) scale(1.1); }
    }

    @media (prefers-reduced-motion: reduce) {
      .banner-img, .banner-aura, .ember { animation: none !important; }
      .ember { display: none; }
    }

    @media (max-width: 980px) {
      .auth-shell {
        flex-direction: column;
      }
      .auth-panel {
        width: 100%;
      }
    }
    @media (max-width: 700px) {
      .promo-panel {
        display: flex !important;
        flex: 0 0 32vh;
        min-height: 210px;
        max-height: 36vh;
        padding: 10px !important;
      }
      .banner-card { border-radius: 18px !important; }
      .banner-card::after { inset: 6px; border-radius: 13px; }
      .promo-wish { display: none !important; }
      .auth-panel {
        flex: 1 0 auto;
        min-height: 500px;
        padding: 10px !important;
        border-left: none !important;
      }
      .auth-shell {
        overflow-y: auto !important;
      }
    }
  `;

  if (!isactive) {
    return (
      <div style={styles.page}>
        <style>{keyframes}</style>
        <div style={{ ...styles.card, textAlign: "center" }}>
          <div style={{ fontSize: "40px", marginBottom: "12px" }}>⚠️</div>
          <p style={{ color: C.crimson, fontWeight: "700", fontSize: "15px" }}>
            Domain Not Active
          </p>
          <p style={{ color: "#6a6f6f", fontSize: "13px" }}>{url}</p>
        </div>
      </div>
    );
  }

  if (!apis) {
    return (
      <div style={styles.page}>
        <style>{keyframes}</style>
        <div style={styles.card}>
          {[100, 60, 80, 40].map((w, i) => (
            <div key={i} style={{
              height: "18px", borderRadius: "8px", marginBottom: "14px",
              background: "rgba(246,196,83,0.28)",
              width: `${w}%`,
              animation: `sparkle ${1.5 + i * 0.3}s ease-in-out infinite`,
            }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{keyframes}</style>

      <div className="auth-shell" style={styles.shell}>
        {/* ---------- Left: banner ---------- */}
        <div className="promo-panel" style={styles.leftPanel}>
          <div className="banner-card" style={styles.bannerCard}>
            <img
              className="banner-img"
              src={`${process.env.PUBLIC_URL}/navratri-banner.png`}
              alt="Shubh Navratri celebration"
              style={styles.navratriImage}
              onError={(e) => console.error("Banner failed to load:", e.target.src)}
            />
            <div className="banner-aura" />
            <div style={styles.vignette} />
            <div style={styles.bottomScrim} />

            {embers.map((e, i) => (
              <span
                key={i}
                className="ember"
                style={{
                  left: `${e.left}%`,
                  width: e.size,
                  height: e.size,
                  "--dur": `${e.dur}s`,
                  "--delay": `${e.delay}s`,
                  "--dx": `${e.dx}px`,
                }}
              />
            ))}

            <p className="promo-wish" style={styles.navratriWish}>
              Wishing you and your family a joyful Navratri filled with peace and prosperity.
            </p>
          </div>
        </div>

        {/* ---------- Right: login form ---------- */}
        <div className="auth-panel" style={styles.rightPane}>
          <div style={styles.card}>
            <div style={styles.cardAccent} />

            <div style={styles.logoWrapper}>
              {image ? (
                <img
                  src={image}
                  alt="Fab Mediatech logo"
                  style={{
                    width: "auto",
                    maxWidth: "180px",
                    height: "64px",
                    objectFit: "contain",
                    borderRadius: "12px",
                  }}
                />
              ) : (
                <div style={{ fontSize: "30px", fontWeight: "900", color: C.crimson }}>Fab Mediatech</div>
              )}
            </div>

            <div style={styles.title}>{company || "Welcome Back"}</div>
            <div style={styles.subtitle}>Sign in to continue to your ERP account</div>

            {error && <div style={styles.errorBox}>{error}</div>}

            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <div style={styles.fieldWrapper}>
                <label style={styles.label(focused === "username")}>Username</label>
                <div style={{ position: "relative" }}>
                  <span style={styles.inputIcon}>👤</span>
                  <input
                    type="text"
                    name="username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleChange}
                    onFocus={() => setFocused("username")}
                    onBlur={() => setFocused("")}
                    required
                    style={styles.input(focused === "username")}
                  />
                </div>
              </div>

              <div style={styles.fieldWrapper}>
                <label style={styles.label(focused === "password")}>Password</label>
                <div style={{ position: "relative" }}>
                  <span style={styles.inputIcon}>🔒</span>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocused("password")}
                    onBlur={() => setFocused("")}
                    required
                    style={{ ...styles.input(focused === "password"), paddingRight: "46px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.passwordToggle}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: "12px" }}>
                <Link to="/forgot-password" className="forgot-link" style={styles.forgotLink}>
                  Forgot password?
                </Link>
              </div>

              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  style={styles.checkboxInput}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms-and-conditions" style={styles.termsLink}>
                    Terms & Conditions
                  </Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={!isChecked}
                className="login-btn"
                style={styles.submitBtn(!isChecked)}
              >
                Sign in →
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;